import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from '../lib/toast';
import { usePolygonDraw } from '../hooks/usePolygonDraw';
import { BASEMAPS, type BasemapId } from '../lib/map-basemaps';

// The STATE's builder.
//
// A subgrantee's builder designs a network. A state's builder does the opposite: it
// isolates the part of an award somebody wants to walk away from, and prices exactly
// that. So this page carries no design tools - no node placement, no segment drawing -
// only selection, and the one number the selection is worth.
//
// Deliberately a separate page from MapPage rather than a mode inside it. MapPage is the
// operator's design surface and the largest file in the app; bolting a regulator's
// workflow into it risks the product we already sell to ISPs.

type Sub = {
  award_id: string; state: string; subgrantee: string;
  project_id: string; project_code: string; project_name: string;
  awarded_bsl_count: number | null; grant_amount_usd: string | null;
  awarded_locations: string;
  // Authoritative, already-labelled figures from /portfolio. The rate is the ORIGINAL
  // grant over the ORIGINAL passing count - the same basis the review engine prices
  // against. Recomputing it here from the live count produces a different number on
  // the same screen, which is exactly the kind of thing a subgrantee's counsel finds.
  award_rate_per_passing_usd?: number | null;
  original_awarded_bsl_count?: number | null;
  currently_awarded_locations?: number | null;
};
type Loc = {
  fcc_location_id: string; address: string | null; city: string | null;
  lng: number | string; lat: number | string;
};

const usd = (v: number | null | undefined, dp = 0) =>
  v == null || !Number.isFinite(Number(v)) ? '—'
    : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: dp });

// Colours carry the argument: green = the subgrantee is driving past this one anyway,
// red = genuinely remote, slate = not yet measured.
const C_PASSED = '#16a34a';
const C_REMOTE = '#dc2626';
const C_PLAIN = '#64748b';


// Numbered steps, because a reviewer arriving at this screen with an email in their hand
// needs to know what to do, not just what exists.
//
// Declared at module scope on purpose. Inside the component this is a new function
// identity on every render, so React remounts the whole panel each time any state
// changes - scroll jumps to the top, focus leaves the input you were typing in.
function Step({ n, title, hint, children }: any) {
  return (
    <div className="bead-section" style={{ marginBottom: 18 }}>
      <div className="design-panel__step" style={{ marginBottom: 8 }}>
        <div className="design-step-num">{n}</div>
        <div>
          <div className="design-panel__step-title">{title}</div>
          {hint && <div className="design-panel__step-hint">{hint}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

// Everything this page draws, bottom to top. Re-applied whenever a layer is (re)added,
// because the order layers happen to be created in is not the order they should stack.
const Z_ORDER = [
  'descope-soil', 'descope-route-casing', 'descope-route-line',
  'lasso', 'awarded', 'descope-crossings',
];
function orderLayers(map: any) {
  for (const id of Z_ORDER) if (map.getLayer(id)) map.moveLayer(id);
}

export default function GrantorBuilderPage() {
  const nav = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [basemap, setBasemap] = useState<BasemapId>('streets');

  const [subgrantee, setSubgrantee] = useState('');
  const [projectId, setProjectId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lasso, setLasso] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [built, setBuilt] = useState<any>(null);   // the priced result
  const [showSoil, setShowSoil] = useState(false);
  // Bumped after a basemap change has finished loading. Every layer effect depends
  // on it, so they all re-run and re-add what setStyle just discarded.
  const [styleEpoch, setStyleEpoch] = useState(0);
  // The soil we last fetched. Held here rather than only inside the map so it can be
  // redrawn after a basemap change, and toggled off and back on without refetching.
  const [soil, setSoil] = useState<{ fc: any; mix: any } | null>(null);
  // Every placement method already priced for the CURRENT selection. "What would
  // this cost built the cheap way?" is the first question a state asks a subgrantee
  // who wants out, and answering it should not mean writing two numbers on a napkin.
  const [pricedBy, setPricedBy] = useState<{ key: string; by: Record<string, number> }>(
    { key: '', by: {} });
  // What the state is assuming about HOW it gets built. Aerial vs buried moves the
  // number more than anything else on this screen, and 'what would it cost aerial?'
  // is a fair question for a regulator to put back to a subgrantee.
  const [buildSpecs, setBuildSpecs] = useState<any>({
    default_method: 'buried', max_drop_ft: 500, corridor_padding_ft: 500,
    avoid_railroads: 'off', avoid_waterways: 'off',
  });

  // ── data ────────────────────────────────────────────────────────────────
  const portfolio = useQuery({
    queryKey: ['grantor-portfolio'],
    queryFn: () => api.get('/descope/portfolio'),
  });
  const rows: Sub[] = portfolio.data?.subgrantees ?? [];
  const subgrantees = useMemo(
    () => Array.from(new Set(rows.map((r) => r.subgrantee))).sort(), [rows]);
  const projects = useMemo(
    () => rows.filter((r) => !subgrantee || r.subgrantee === subgrantee), [rows, subgrantee]);
  const project = projects.find((p) => p.project_id === projectId) ?? null;

  const locsQ = useQuery({
    queryKey: ['grantor-locs', projectId],
    queryFn: () => api.get(`/descope/projects/${projectId}/locations`),
    enabled: !!projectId,
  });
  const locations: Loc[] = useMemo(() => locsQ.data?.locations ?? [], [locsQ.data]);

  const refQ = useQuery({
    queryKey: ['grantor-ref', projectId],
    queryFn: () => api.get(`/descope/projects/${projectId}/reference-build`),
    enabled: !!projectId,
  });
  const hasRef = !!refQ.data?.segment_count;

  // Which of the selected are being driven past anyway. Fast (~200ms) because it reads
  // the award, not a design - see descope-reference.ts for why it is not measured
  // against the cached build.
  const selectedIds = useMemo(() => [...selected], [selected]);
  // Stable identity for "the set currently under review".
  const selectionKey = useMemo(() => [...selectedIds].sort().join(','), [selectedIds]);
  const prox = useQuery({
    queryKey: ['grantor-prox', projectId, selectedIds.join(',')],
    queryFn: () => api.post('/descope/proximity-preview',
      { project_id: projectId, location_ids: selectedIds }),
    enabled: !!projectId && selectedIds.length > 0,
  });
  // Locations the design never reached - returned by the review, so only known after
  // a build.
  const unreached = useMemo<Set<string>>(
    () => new Set((built?.rev?.passings_basis?.unreached ?? []) as string[]),
    [built],
  );

  const proxById = useMemo(() => {
    const m = new Map<string, { distance_ft: number | null; passed_anyway: boolean }>();
    for (const l of (prox.data?.locations ?? [])) m.set(l.id, l);
    return m;
  }, [prox.data]);
  const passedAnyway = prox.data?.passed_anyway ?? 0;

  // ── map ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAPS[basemap].style,
      center: [-90.4, 32.7],
      zoom: 8,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    // 'load' fires once, and only after the first frame is actually painted. If it is
    // missed - a tab that was never composited, a style that resolved before the
    // listener attached - the page sits there with a live map and no layers on it and
    // no way back. Take either signal.
    const ready = () => { mapRef.current = map; setMapReady(true); };
    map.on('load', ready);
    map.on('styledata', () => { if (map.isStyleLoaded()) ready(); });
    return () => { map.remove(); mapRef.current = null; setMapReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Awarded locations as dots, coloured by what we know about them.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const fc: any = {
      type: 'FeatureCollection',
      features: locations.map((p) => {
        const id = p.fcc_location_id;
        const px = proxById.get(id);
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [Number(p.lng), Number(p.lat)] },
          properties: {
            id,
            selected: selected.has(id),
            // 'unreached' matters: the design could not get within a drop of it, so it
            // is in the request but NOT in the cost. Leaving it identical to the others
            // is how a reviewer ends up quoting a number that covers fewer homes than
            // they think.
            state: !selected.has(id) ? 'plain'
              : unreached.has(id) ? 'unreached'
              : px ? (px.passed_anyway ? 'passed' : 'remote') : 'pending',
            distance_ft: px?.distance_ft ?? null,
            address: p.address ?? '',
          },
        };
      }),
    };
    const paint: any = {
      'circle-radius': ['case', ['get', 'selected'], 7, 4],
      'circle-color': ['match', ['get', 'state'],
        'passed', C_PASSED, 'remote', C_REMOTE, 'pending', '#f59e0b',
        'unreached', '#111827', C_PLAIN],
      'circle-stroke-width': ['case', ['get', 'selected'], 2, 0.5],
      'circle-stroke-color': '#ffffff',
    };
    const src = map.getSource('awarded') as any;
    if (src) { src.setData(fc); map.setPaintProperty('awarded', 'circle-color', paint['circle-color']); }
    else {
      map.addSource('awarded', { type: 'geojson', data: fc } as any);
      map.addLayer({ id: 'awarded', type: 'circle', source: 'awarded', paint } as any);
      orderLayers(map);
      map.on('mouseenter', 'awarded', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'awarded', () => { map.getCanvas().style.cursor = ''; });
    }
  }, [locations, selected, proxById, unreached, mapReady, styleEpoch]);

  // What the map should be looking at, in order of what the reviewer is doing:
  // the designed route, else the locations under review, else the whole award.
  const frameTarget = useMemo(() => {
    const routeFc = built?.route?.geojson;
    const coords: [number, number][] = [];
    const walk = (g: any) => {
      if (!g) return;
      if (g.type === 'Point') coords.push(g.coordinates);
      else if (g.type === 'LineString' || g.type === 'MultiPoint') coords.push(...g.coordinates);
      else if (g.type === 'MultiLineString' || g.type === 'Polygon') for (const p of g.coordinates) coords.push(...p);
      else if (g.type === 'GeometryCollection') g.geometries.forEach(walk);
    };
    if (routeFc?.features?.length) {
      for (const f of routeFc.features) walk(f.geometry);
      if (coords.length) return { key: `route:${built?.rev?.review_id ?? ''}`, coords };
    }
    if (selectedIds.length) {
      const set = new Set(selectedIds);
      for (const l of locations) {
        if (set.has(l.fcc_location_id)) coords.push([Number(l.lng), Number(l.lat)]);
      }
      if (coords.length) return { key: `sel:${selectionKey}`, coords };
    }
    if (locations.length) {
      return { key: `award:${projectId}`, coords: locations.map((l) => [Number(l.lng), Number(l.lat)] as [number, number]) };
    }
    return null;
  }, [built, selectedIds, selectionKey, locations, projectId]);

  // Move only when the SUBJECT changes, not on every render - otherwise the map fights
  // the reviewer for control of the viewport every time they pan.
  const framedKey = useRef<string>('');
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !frameTarget) return;
    if (framedKey.current === frameTarget.key) return;
    framedKey.current = frameTarget.key;
    const b = new maplibregl.LngLatBounds();
    for (const c of frameTarget.coords) b.extend(c);
    // A single location has zero extent; padding alone would still leave maxZoom to
    // decide, so cap it rather than dropping the reviewer into the void.
    map.fitBounds(b, { padding: 80, maxZoom: 16, duration: 700 });
  }, [frameTarget, mapReady]);

  // ── selection: lasso, and click-to-toggle ───────────────────────────────
  const inRing = useCallback((ring: [number, number][]) => {
    // ray casting; the ring comes back closed from the hook
    const hit = (x: number, y: number) => {
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
      }
      return inside;
    };
    // Only awarded BSLs are selectable. The design still routes past ordinary buildings
    // to reach them, but a state is deciding about awarded passings, not houses.
    const picked = locations.filter((l) => hit(Number(l.lng), Number(l.lat)))
      .map((l) => l.fcc_location_id);
    setSelected((prev) => new Set([...prev, ...picked]));
    toast.success(`${picked.length} awarded locations added to the selection`);
  }, [locations]);

  const poly = usePolygonDraw(mapRef, { active: lasso, onFinish: (ring) => { inRing(ring); setLasso(false); } });

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const onClick = (e: any) => {
      if (lasso) { poly.appendVertex([e.lngLat.lng, e.lngLat.lat]); return; }
      const f = map.queryRenderedFeatures(e.point, { layers: ['awarded'] })[0];
      if (!f) return;
      const id = String((f.properties as any).id);
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    };
    const onDbl = () => { if (lasso) poly.finish(); };
    map.on('click', onClick);
    map.on('dblclick', onDbl);
    return () => { map.off('click', onClick); map.off('dblclick', onDbl); };
  }, [mapReady, lasso, poly]);

  // draw the in-progress lasso
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const fc: any = { type: 'FeatureCollection', features: poly.ring.length >= 2
      ? [{ type: 'Feature', geometry: { type: 'LineString', coordinates: poly.ring }, properties: {} }] : [] };
    const src = map.getSource('lasso') as any;
    if (src) src.setData(fc);
    else {
      map.addSource('lasso', { type: 'geojson', data: fc } as any);
      map.addLayer({ id: 'lasso', type: 'line', source: 'lasso',
        paint: { 'line-color': '#2563eb', 'line-width': 2, 'line-dasharray': [2, 1] } } as any);
      orderLayers(map);
    }
  }, [poly.ring, mapReady, styleEpoch]);


  // The designed route and its crossings, drawn from `built`. These used to be painted
  // once inside the mutation's onSuccess, which meant a basemap change wiped them and
  // nothing ever put them back.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const fc = built?.route?.geojson ?? { type: 'FeatureCollection', features: [] };
    const src = map.getSource('descope-route') as any;
    if (src) src.setData(fc);
    else {
      map.addSource('descope-route', { type: 'geojson', data: fc } as any);
      // Backbone heavy, drops hairline: at a glance you can see what is trunk and what
      // is the last hundred feet to a house.
      map.addLayer({
        id: 'descope-route-casing', type: 'line', source: 'descope-route',
        paint: { 'line-color': '#0f172a', 'line-width': ['case', ['==', ['get', 'kind'], 'backbone'], 6, 3], 'line-opacity': 0.5 },
      } as any);
      map.addLayer({
        id: 'descope-route-line', type: 'line', source: 'descope-route',
        paint: { 'line-color': ['case', ['==', ['get', 'kind'], 'backbone'], '#f59e0b', '#fbbf24'],
                 'line-width': ['case', ['==', ['get', 'kind'], 'backbone'], 3.5, 1.5] },
      } as any);
      orderLayers(map);
    }
  }, [built, mapReady, styleEpoch]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const cx = built?.crossings;
    const feats = cx?.usable
      ? [...((cx.waterways?.features?.features) ?? []), ...((cx.railroads?.features?.features) ?? [])]
      : [];
    const cfc: any = { type: 'FeatureCollection', features: feats };
    const src = map.getSource('descope-crossings') as any;
    if (src) src.setData(cfc);
    else {
      map.addSource('descope-crossings', { type: 'geojson', data: cfc } as any);
      // Crossings are EVENTS on the route, not locations. Drawn as circles they read as
      // awarded passings and a reviewer cannot tell a creek from a house.
      map.addLayer({
        id: 'descope-crossings', type: 'symbol', source: 'descope-crossings',
        layout: {
          'text-field': ['case', ['==', ['get', 'kind'], 'railroads'], '\u2715', '\u25C6'],
          'text-size': 14, 'text-allow-overlap': true,
        },
        paint: {
          'text-color': ['case', ['==', ['get', 'kind'], 'railroads'], '#ef4444', '#3b82f6'],
          'text-halo-color': '#ffffff', 'text-halo-width': 2,
        },
      } as any);
      orderLayers(map);
    }
  }, [built, mapReady, styleEpoch]);

  // Soil, from state, so it survives a basemap change and can be toggled without
  // refetching.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const fc = (showSoil && soil?.fc) ? soil.fc : { type: 'FeatureCollection', features: [] };
    const src = map.getSource('descope-soil') as any;
    if (src) src.setData(fc);
    else {
      map.addSource('descope-soil', { type: 'geojson', data: fc } as any);
      map.addLayer({
        id: 'descope-soil', type: 'fill', source: 'descope-soil',
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#94a3b8'],
          'fill-opacity': 0.45,
          'fill-outline-color': '#ffffff',
        },
      } as any);
      orderLayers(map);
    }
  }, [soil, showSoil, mapReady, styleEpoch]);

  // ── paste / upload a BSL list ───────────────────────────────────────────
  const resolveList = useMutation({
    mutationFn: () => {
      const ids = pasted.split(/[\s,;]+/).map((x) => x.trim().replace(/^["']|["']$/g, ''))
        .filter((x) => x && !/^(bsl|location_id|fcc_location_id|id)$/i.test(x));
      return api.post('/descope/resolve', { project_id: projectId, location_ids: ids });
    },
    onSuccess: (r: any) => {
      setSelected((prev) => new Set([...prev, ...(r.priceable ?? [])]));
      const bad = (r.counts?.unknown ?? 0) + (r.counts?.in_another_project ?? 0);
      if (bad) toast.error(`${r.counts.priceable} matched · ${bad} not in this award`);
      else toast.success(`${r.counts.priceable} locations matched this award`);
      setPasteOpen(false);
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  // Design the network for exactly the selected locations and DRAW it. This is the
  // moment the number stops being an assertion: the state sees the route it is paying
  // for, what it crosses, and the ground it cuts through.
  const buildPlant = useMutation({
    mutationFn: async () => {
      const rev: any = await api.post('/descope/review', {
        project_id: projectId,
        excluded_location_ids: selectedIds,
        specs: buildSpecs,
      });
      (rev as any).__method = buildSpecs.default_method;
      if (rev?.error) throw new Error(rev.error);
      const id = rev.review_id;
      setReviewId(id ?? null);
      setSoil(null); setShowSoil(false);   // new route, different ground
      // Route and crossings hang off the stored review, so they survive the scratch
      // plant being torn down and can be redrawn months later without redesigning.
      const [route, crossings] = await Promise.all([
        id ? api.get(`/descope/reviews/${id}/route`).catch(() => null) : null,
        id ? api.get(`/descope/reviews/${id}/crossings`).catch(() => null) : null,
      ]);
      return { rev, route, crossings };
    },
    onSuccess: (out: any) => {
      setBuilt(out);
      const m = out?.rev?.__method;
      const c = Number(out?.rev?.cost?.total_cost_usd);
      const key = selectionKey;
      if (m && Number.isFinite(c)) {
        setPricedBy((prev) => ({
          key,
          by: { ...(prev.key === key ? prev.by : {}), [m]: c },
        }));
      }
      toast.success('Network designed');
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  // SSURGO soil for this award, coloured by construction method - the ground the route
  // has to get through, which is where "too expensive" usually actually lives.
  const loadSoil = useCallback(async () => {
    if (showSoil) { setShowSoil(false); return; }
    if (soil) { setShowSoil(true); return; }
    if (!reviewId) { toast.error('Build the network first — soil is shown for the route.'); return; }
    try {
      // Scoped to the priced route. Asking for the whole award times out (398 sq mi
      // against 700k SSURGO polygons) and answers the wrong question anyway.
      const r: any = await api.get(`/descope/reviews/${reviewId}/soil`);
      if (r?.usable === false) { toast.error('Soil unavailable: ' + (r.reason ?? 'unknown')); return; }
      const fc = r?.geojson ?? r;
      if (!fc?.features?.length) { toast.error('No soil data covers this route.'); return; }
      setSoil({ fc, mix: r?.mix ?? null });
      setShowSoil(true);
    } catch (e: any) { toast.error('Soil unavailable: ' + String(e?.message ?? e)); }
  }, [reviewId, showSoil, soil]);

  const buildRef = useMutation({
    mutationFn: () => api.post(`/descope/projects/${projectId}/reference-build`, {}),
    onSuccess: () => { toast.success('Reference build ready'); refQ.refetch(); },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  // ── panel ───────────────────────────────────────────────────────────────
  const award = project ? Number(project.grant_amount_usd ?? 0) : 0;
  const awardedN = project ? Number(project.awarded_locations ?? 0) : 0;
  const originalN = project
    ? Number(project.original_awarded_bsl_count ?? project.awarded_bsl_count ?? awardedN)
    : 0;
  // Take the engine's rate when it is offered; only fall back to arithmetic here.
  const awardRate = project?.award_rate_per_passing_usd != null
    ? Number(project.award_rate_per_passing_usd)
    : (originalN ? award / originalN : null);
  const remoteN = selectedIds.length - passedAnyway;
  const priceCompare = pricedBy.key === selectionKey ? pricedBy.by : {};
  const comp = built?.route?.composition ?? null;
  const rev = built?.rev ?? null;
  const cx = built?.crossings ?? null;


  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)' }}>
      <div ref={containerRef} style={{ flex: 1, position: 'relative' }} />

      <aside style={{
        width: 400, borderLeft: '1px solid var(--border)', background: 'var(--surface)',
        overflowY: 'auto', padding: 18,
      }}>
        <h2 style={{ margin: '0 0 2px', fontSize: 17 }}>De-Scope Builder</h2>
        <p className="design-panel__step-hint" style={{ marginTop: 0, marginBottom: 18 }}>
          Isolate the locations a subgrantee wants released and price exactly those.
        </p>

        <Step n={1} title="Whose award" hint="Pick the subgrantee, then the award in question.">
          <div style={{ display: 'grid', gap: 8 }}>
            <select value={subgrantee}
                    onChange={(e) => { setSubgrantee(e.target.value); setProjectId(''); setSelected(new Set()); setBuilt(null); }}>
              <option value="">All subgrantees</option>
              {subgrantees.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <select value={projectId}
                    onChange={(e) => { setProjectId(e.target.value); setSelected(new Set()); setBuilt(null); }}>
              <option value="">Select an award…</option>
              {projects.map((x) => (
                <option key={x.project_id} value={x.project_id}>
                  {x.project_code} — {x.awarded_locations} passings
                </option>
              ))}
            </select>
          </div>

          {project && (
            <div className="bead-kpis" style={{ marginTop: 10 }}>
              <div className="bead-kpi">
                <div className="bead-kpi__label" style={{ whiteSpace: 'nowrap' }}>Award</div>
                <div className="bead-kpi__value">{usd(award)}</div>
              </div>
              <div className="bead-kpi">
                <div className="bead-kpi__label" style={{ whiteSpace: 'nowrap' }}>Awarded</div>
                <div className="bead-kpi__value">{awardedN}</div>
              </div>
              <div className="bead-kpi">
                <div className="bead-kpi__label" style={{ whiteSpace: 'nowrap' }}>Rate</div>
                <div className="bead-kpi__value">{usd(awardRate)}</div>
              </div>
            </div>
          )}

          {project && originalN > awardedN && (
            <div className="design-panel__step-hint" style={{ marginTop: 6 }}>
              {originalN - awardedN} of {originalN} already released. Rate held on the
              original basis.
            </div>
          )}
        </Step>

        {project && (
          <Step n={2} title="What they want released"
                hint="Lasso the map, paste their BSL list, or click individual dots.">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className={`btn${lasso ? ' btn--primary' : ''}`}
                      onClick={() => { setLasso((v) => !v); poly.clear(); }}>
                {lasso ? 'Drawing — double-click to close' : 'Lasso on map'}
              </button>
              <button className="btn" onClick={() => setPasteOpen((v) => !v)}>Paste / upload list</button>
              {selectedIds.length > 0 &&
                <button className="btn" onClick={() => { setSelected(new Set()); setBuilt(null); }}>
                  Clear {selectedIds.length}
                </button>}
            </div>

            {pasteOpen && (
              <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                <textarea rows={4} value={pasted} onChange={(e) => setPasted(e.target.value)}
                          placeholder="Paste their BSL numbers — commas, spaces or one per line"
                          style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label className="btn" style={{ cursor: 'pointer' }}>
                    Upload CSV
                    <input type="file" accept=".csv,.txt" style={{ display: 'none' }}
                           onChange={(e) => {
                             const f = e.target.files?.[0]; if (!f) return;
                             const fr = new FileReader();
                             fr.onload = () => setPasted(String(fr.result || ''));
                             fr.readAsText(f);
                           }} />
                  </label>
                  <button className="btn btn--primary" disabled={!pasted.trim() || resolveList.isPending}
                          onClick={() => resolveList.mutate()}>
                    {resolveList.isPending ? 'Matching…' : 'Match against award'}
                  </button>
                </div>
              </div>
            )}

            {selectedIds.length > 0 && (
              <div className="bom-section" style={{ marginTop: 12 }}>
                <div className="bom-section__title">Under review — {selectedIds.length} passings</div>
                <div className="bom-row">
                  <span className="bom-dot" style={{ background: 'var(--success)' }} />
                  <span style={{ flex: 1 }}>Passed anyway</span>
                  <b>{passedAnyway}</b>
                </div>
                <div className="bom-row">
                  <span className="bom-dot" style={{ background: 'var(--danger)' }} />
                  <span style={{ flex: 1 }}>Genuinely remote</span>
                  <b>{remoteN}</b>
                </div>
                {prox.data?.usable && passedAnyway > 0 && (
                  <div className="design-panel__step-hint">
                    Within {prox.data.drop_ft} ft of a passing they still owe.
                  </div>
                )}
              </div>
            )}
          </Step>
        )}

        {project && selectedIds.length > 0 && (
          <Step n={3} title="Price it" hint="Designs fibre to exactly these locations and draws it.">
            <div className="card" style={{ marginBottom: 10 }}>
              <div className="card__header">Build assumptions</div>
              <div className="card__body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Placement
                  <select value={buildSpecs.default_method} style={{ width: '100%' }}
                          onChange={(e) => setBuildSpecs({ ...buildSpecs, default_method: e.target.value })}>
                    <option value="buried">Buried</option>
                    <option value="aerial">Aerial</option>
                    <option value="conduit">Conduit</option>
                    <option value="direct_bury">Direct bury</option>
                    <option value="micro_trench">Micro-trench</option>
                  </select>
                </label>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Max drop (ft)
                  <input type="number" value={buildSpecs.max_drop_ft} style={{ width: '100%' }}
                         onChange={(e) => setBuildSpecs({ ...buildSpecs, max_drop_ft: Number(e.target.value) || 0 })} />
                </label>
              </div>
            </div>

            <button className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }}
                    disabled={buildPlant.isPending} onClick={() => buildPlant.mutate()}>
              {buildPlant.isPending ? 'Designing…' : `Build the network for these ${selectedIds.length}`}
            </button>
            {buildPlant.isPending && (
              <div className="design-panel__step-hint" style={{ marginTop: 6 }}>
                Routing fibre over the real road network. This takes a moment.
              </div>
            )}
          </Step>
        )}

        {rev && (
          <Step n={4} title="What it costs" hint="The estimate, and what it rests on.">
            <div className="bom-grid">
              <div className="bom-tile">
                <div className="bom-tile__label">Cost to build</div>
                <div className="bom-tile__value">{usd(rev.cost?.total_cost_usd)}</div>
              </div>
              <div className="bom-tile">
                <div className="bom-tile__label">Per passing</div>
                <div className="bom-tile__value">{usd(rev.cost?.cost_per_passing_usd)}</div>
              </div>
              <div className="bom-tile">
                <div className="bom-tile__label">Award rate</div>
                <div className="bom-tile__value">{usd(rev.exposure?.award_rate_per_passing_usd)}</div>
              </div>
            </div>

            {rev.exposure?.build_vs_award_ratio != null && (
              <div className={`bom-pencils ${rev.exposure.build_vs_award_ratio > 1 ? 'pencils-no' : 'pencils-yes'}`}
                   style={{ marginTop: 10 }}>
                <div className="bom-pencils__icon">
                  {rev.exposure.build_vs_award_ratio > 1 ? '!' : '✓'}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="bom-pencils__headline">
                    {rev.exposure.build_vs_award_ratio.toFixed(2)}× the award rate
                  </div>
                  <div className="bom-pencils__sub">
                    {rev.exposure.build_vs_award_ratio > 1
                      ? 'Costs more than the award assumed — the request is supported by the cost evidence.'
                      : 'Within the award rate — the cost evidence does not support release.'}
                  </div>
                </div>
              </div>
            )}

            {Object.keys(priceCompare).length > 1 && (
              <div className="bom-section" style={{ marginTop: 12 }}>
                <div className="bom-section__title">Same request, priced both ways</div>
                {Object.entries(priceCompare)
                  .sort((a, b) => a[1] - b[1])
                  .map(([m, c], i) => (
                    <div key={m} className={`bom-row${i === 0 ? '' : ' bom-row--total'}`}>
                      <span style={{ flex: 1, textTransform: 'capitalize' }}>
                        {m.replace(/_/g, ' ')}
                      </span>
                      <b>{usd(c)}</b>
                    </div>
                  ))}
                <div className="design-panel__step-hint">
                  Change the placement above and price it again.
                </div>
              </div>
            )}

            {comp && (
              <div className="bom-section" style={{ marginTop: 12 }}>
                <div className="bom-section__title">What the plant is doing</div>
                <div className="bom-row">
                  <span style={{ flex: 1 }}>Alongside the requested locations</span>
                  <b>{(comp.serving_m / 1609.34).toFixed(0)} mi</b>
                </div>
                <div className="bom-row">
                  <span style={{ flex: 1 }}>Connecting them to each other</span>
                  <b>{(comp.connecting_m / 1609.34).toFixed(0)} mi</b>
                </div>
                <div className="bom-row bom-row--total">
                  <span style={{ flex: 1 }}>Total plant</span>
                  <b>{(comp.total_m / 1609.34).toFixed(0)} mi</b>
                </div>
                <div className="design-panel__step-hint">{comp.note}</div>
              </div>
            )}

            {unreached.size > 0 && (
              <div className="bom-section" style={{ marginTop: 12 }}>
                <div className="bom-section__title">Not reached</div>
                <div className="bom-row">
                  <span className="bom-dot" style={{ background: '#111827' }} />
                  <span style={{ flex: 1 }}>In the request but not in the cost</span>
                  <b>{unreached.size}</b>
                </div>
                <div className="design-panel__step-hint">
                  No structure found, or no road within the drop limit. Shown black.
                </div>
              </div>
            )}

            {cx?.usable && (
              <div className="bom-section" style={{ marginTop: 12 }}>
                <div className="bom-section__title">Crossings on this route</div>
                <div className="crossing-row">
                  <span className="dot dot--waterway" />
                  <div style={{ flex: 1 }}>Waterways</div>
                  <b>{cx.waterways?.count ?? '—'}</b>
                </div>
                <div className="crossing-row">
                  <span className="dot dot--railroad" />
                  <div style={{ flex: 1 }}>Railroads</div>
                  <b>{cx.railroads?.count ?? '—'}</b>
                </div>
                <div className="design-panel__step-hint">
                  Detected, not yet priced — cost risk on top of the figure above.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button className={`btn${showSoil ? ' btn--primary' : ''}`} onClick={loadSoil}>
                {showSoil ? 'Hide soil conditions' : 'Show soil conditions'}
              </button>
              {showSoil && soil && (
                <div className="bom-section" style={{ marginTop: 10 }}>
                  <div className="bom-section__title">Ground along this route</div>
                  <div className="bom-row">
                    <span className="bom-dot" style={{ background: '#b91c1c' }} />
                    <span style={{ flex: 1 }}>Shallow bedrock — rock trenching</span>
                    <b>{soil.mix?.rock_pct ?? 0}%</b>
                  </div>
                  <div className="bom-row">
                    <span className="bom-dot" style={{ background: '#2563eb' }} />
                    <span style={{ flex: 1 }}>Poorly drained or open water</span>
                    <b>{soil.mix?.wet_pct ?? 0}%</b>
                  </div>
                  <div className="bom-row">
                    <span className="bom-dot" style={{ background: '#16a34a' }} />
                    <span style={{ flex: 1 }}>Ordinary trenching</span>
                    <b>{soil.mix?.normal_pct ?? 0}%</b>
                  </div>
                  <div className="design-panel__step-hint">
                    {soil.fc?.features?.length ?? 0} SSURGO map units under the route. Not priced.
                  </div>
                </div>
              )}
            </div>
          </Step>
        )}

        {project && selectedIds.length > 0 && (
          <Step n={5} title="Put it on the record"
                hint="The full marginal analysis runs there and the decision is signed.">
            <button className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      // The selection travels in session storage under a short key. The
                      // address bar keeps the project and the count so the URL still says
                      // what it is, and can be read back by a human.
                      const key = `descope-handoff:${projectId}`;
                      try { sessionStorage.setItem(key, JSON.stringify(selectedIds)); } catch { /* private mode */ }
                      nav(`/descope?project=${projectId}&handoff=${selectedIds.length}`);
                    }}>
              Send {selectedIds.length} to De-Scope Review →
            </button>
          </Step>
        )}

        <div className="bead-section">
          <div className="bom-section__title">Reference build</div>
          {hasRef ? (
            <div className="bom-row">
              <span style={{ flex: 1 }}>{refQ.data.segment_count} segments</span>
              <b>{usd(Number(refQ.data.total_cost_usd))}</b>
            </div>
          ) : (
            <div className="design-panel__step-hint">Not built for this award yet.</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn" disabled={buildRef.isPending || !projectId}
                    onClick={() => buildRef.mutate()}>
              {buildRef.isPending ? 'Designing the whole award…' : hasRef ? 'Rebuild' : 'Build reference'}
            </button>
            <select value={basemap} onChange={(e) => {
              const id = e.target.value as BasemapId;
              setBasemap(id);
              const map = mapRef.current;
              if (!map) return;
              map.setStyle(BASEMAPS[id].style);
              // Both signals: 'styledata' can land while the new style is still
              // settling, 'idle' can be delayed behind tile loads. Re-adding is
              // idempotent - every layer effect checks getSource first - so the
              // cheapest correct thing is to react to whichever arrives.
              map.once('styledata', () => setStyleEpoch((n) => n + 1));
              map.once('idle', () => setStyleEpoch((n) => n + 1));
            }}>
              {Object.entries(BASEMAPS).map(([id, b]: any) => (
                <option key={id} value={id}>{b.label ?? id}</option>
              ))}
            </select>
          </div>
        </div>
      </aside>
    </div>
  );
}
